import React, { useEffect, useRef, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Download, CheckCircle2, XCircle, Award, ClipboardList, FileText, Trash2, HandHeart } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const ABS = (u) => (u && u.startsWith("/api/") ? `${API.replace(/\/api$/, "")}${u}` : u);

const DEFAULT_FORM = {
  cert_number: "",
  hours: "40",
  work_type: "TEACHING",
  issue_date: new Date().toISOString().slice(0, 10),
  start_date: "",
  end_date: "",
  grade: "A",
};

export default function AdminSocialWorkApplications() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [issueFor, setIssueFor] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [issuing, setIssuing] = useState(false);
  const fileInputs = useRef({});
  const attendanceInputs = useRef({});

  useEffect(() => { load(); }, [filter]);
  const load = async () => {
    const r = await api.get(`/admin/social-work-applications${filter ? `?status=${filter}` : ""}`);
    setItems(r.data || []);
  };

  const updateStatus = async (id, status) => {
    if (status === "rejected" && !window.confirm("Reject and DELETE this application permanently?")) return;
    try {
      const { data } = await api.put(`/admin/social-work-applications/${id}/status?status=${status}`);
      if (data?.deleted) {
        toast.success("Application rejected and removed");
      } else {
        toast.success(`Marked ${status}`);
      }
      load();
    } catch { toast.error("Failed"); }
  };

  const uploadCert = async (id, file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/admin/social-work-applications/${id}/certificate`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Certificate uploaded"); load();
    } catch { toast.error("Upload failed"); }
  };

  const uploadAttendance = async (id, file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/admin/social-work-applications/${id}/attendance`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Attendance sheet uploaded"); load();
    } catch { toast.error("Attendance upload failed"); }
  };

  const removeAttendance = async (id) => {
    if (!window.confirm("Remove the uploaded attendance sheet?")) return;
    try {
      await api.delete(`/admin/social-work-applications/${id}/attendance`);
      toast.success("Attendance removed"); load();
    } catch { toast.error("Remove failed"); }
  };

  const openIssueModal = (a) => {
    setIssueFor(a);
    setForm({
      ...DEFAULT_FORM,
      cert_number: a.certificate_no?.replace(/^PMR\/26\//i, "") || "",
      issue_date: new Date().toISOString().slice(0, 10),
    });
  };

  const submitIssue = async () => {
    if (!form.cert_number) { toast.error("Certificate number is required"); return; }
    if (!form.hours || !form.work_type) { toast.error("Hours and work type are required"); return; }
    setIssuing(true);
    try {
      const { data } = await api.post(
        `/admin/social-work-applications/${issueFor.id}/auto-certificate`,
        form,
      );
      toast.success(`Certificate ${data.certificate_no} issued`);
      const es = data.email_status || {};
      if (es.sent) {
        toast.success("📧 Email sent to student");
      } else if (es.error) {
        toast.error(`Email NOT sent: ${es.error}. Check Resend domain/SENDER_EMAIL.`, { duration: 8000 });
      }
      setIssueFor(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to issue certificate");
    } finally { setIssuing(false); }
  };

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between">
        <h1 className="heading text-2xl font-bold flex items-center gap-2"><HandHeart className="text-amber-500" size={24} /> Social Work Applications</h1>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border border-slate-300 text-sm"
          data-testid="socwork-filter-select"
        >
          <option value="">All</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="p-3 font-semibold">Applicant</th>
              <th className="p-3 font-semibold">Programme</th>
              <th className="p-3 font-semibold">College</th>
              <th className="p-3 font-semibold">Status</th>
              <th className="p-3 font-semibold">Certificate</th>
              <th className="p-3 font-semibold">Attendance</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-t border-slate-100" data-testid={`socwork-app-row-${a.id}`}>
                <td className="p-3">
                  <div className="font-semibold">{a.name}</div>
                  <div className="text-xs text-slate-500">{a.email} · {a.phone}</div>
                </td>
                <td className="p-3">{a.program_title}</td>
                <td className="p-3 text-slate-600">{a.college}</td>
                <td className="p-3">
                  <span className={`text-[10px] px-2 py-1 rounded font-bold uppercase ${a.status === "approved" ? "bg-emerald-100 text-emerald-700" : a.status === "rejected" ? "bg-red-100 text-red-700" : a.status === "completed" ? "bg-indigo-100 text-indigo-700" : "bg-amber-100 text-amber-700"}`}>
                    {a.status}
                  </span>
                </td>
                <td className="p-3">
                  {a.certificate_url ? (
                    <a href={ABS(a.certificate_url)} target="_blank" rel="noreferrer" className="text-brand font-semibold inline-flex items-center gap-1">
                      <Download size={14} /> {a.certificate_no || "View"}
                    </a>
                  ) : <span className="text-xs text-slate-400">—</span>}
                </td>
                <td className="p-3">
                  {a.attendance_url ? (
                    <div className="inline-flex items-center gap-1.5">
                      <a href={ABS(a.attendance_url)} target="_blank" rel="noreferrer" className="text-brand font-semibold inline-flex items-center gap-1 text-xs">
                        <FileText size={13} /> Sheet
                      </a>
                      <button onClick={() => removeAttendance(a.id)} className="text-red-500 hover:text-red-700" title="Remove attendance" data-testid={`del-socwork-attend-${a.id}`}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ) : <span className="text-xs text-slate-400">—</span>}
                </td>
                <td className="p-3 text-right">
                  <div className="inline-flex gap-1 items-center">
                    {a.status !== "approved" && (
                      <button onClick={() => updateStatus(a.id, "approved")} className="p-2 text-emerald-600 hover:bg-emerald-50 rounded" title="Approve" data-testid={`socwork-approve-${a.id}`}><CheckCircle2 size={14} /></button>
                    )}
                    {a.status !== "rejected" && (
                      <button onClick={() => updateStatus(a.id, "rejected")} className="p-2 text-red-600 hover:bg-red-50 rounded" title="Reject" data-testid={`socwork-reject-${a.id}`}><XCircle size={14} /></button>
                    )}

                    {/* Attendance upload */}
                    <input
                      type="file"
                      accept=".pdf,.xls,.xlsx,.csv,.png,.jpg,.jpeg"
                      ref={(el) => (attendanceInputs.current[a.id] = el)}
                      onChange={(e) => e.target.files?.[0] && uploadAttendance(a.id, e.target.files[0])}
                      className="hidden"
                    />
                    <button onClick={() => attendanceInputs.current[a.id]?.click()} className="p-2 text-indigo-600 hover:bg-indigo-50 rounded" title="Upload attendance sheet" data-testid={`socwork-upload-attend-${a.id}`}>
                      <ClipboardList size={14} />
                    </button>

                    {/* Manual upload */}
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      ref={(el) => (fileInputs.current[a.id] = el)}
                      onChange={(e) => e.target.files?.[0] && uploadCert(a.id, e.target.files[0])}
                      className="hidden"
                    />
                    <button onClick={() => fileInputs.current[a.id]?.click()} className="p-2 text-brand hover:bg-blue-50 rounded" title="Upload certificate file" data-testid={`socwork-upload-cert-${a.id}`}>
                      <Upload size={14} />
                    </button>

                    {/* Issue Appreciation cert — only after approval */}
                    <button
                      onClick={() => openIssueModal(a)}
                      disabled={!["approved", "completed"].includes(a.status)}
                      className="p-2 text-[#F59E0B] hover:bg-amber-50 disabled:opacity-30 disabled:cursor-not-allowed rounded"
                      title={["approved", "completed"].includes(a.status) ? "Issue Appreciation Certificate" : "Approve the application first"}
                      data-testid={`socwork-auto-cert-${a.id}`}
                    >
                      <Award size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-slate-500">No social-work applications.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!issueFor} onOpenChange={(o) => !o && setIssueFor(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="socwork-issue-modal">
          <DialogHeader>
            <DialogTitle>Issue Certificate of Appreciation</DialogTitle>
            <DialogDescription>For <b>{issueFor?.name}</b> — {issueFor?.program_title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Certificate Number</label>
              <div className="mt-1 flex items-center rounded-lg border border-slate-300 focus-within:border-amber-500 overflow-hidden">
                <span className="px-3 py-2 bg-slate-50 text-slate-500 text-sm font-mono">PMR/26/</span>
                <input
                  value={form.cert_number}
                  onChange={(e) => setForm({ ...form, cert_number: e.target.value })}
                  placeholder="045"
                  className="flex-1 px-3 py-2 outline-none text-sm font-mono"
                  data-testid="socwork-cert-number-input"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Hours Completed</label>
              <div className="mt-1 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, hours: String(Math.max(0, (parseInt(form.hours) || 0) - 5)) })}
                  className="w-10 h-10 rounded-lg border border-slate-300 hover:bg-slate-50 font-bold text-lg text-slate-700"
                  data-testid="socwork-hours-dec"
                  title="Decrease by 5"
                >−</button>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={form.hours}
                  onChange={(e) => setForm({ ...form, hours: e.target.value })}
                  placeholder="40"
                  className="flex-1 px-3 py-2 rounded-lg border border-slate-300 focus:border-amber-500 outline-none text-center font-mono text-lg font-bold"
                  data-testid="socwork-hours-input"
                />
                <button
                  type="button"
                  onClick={() => setForm({ ...form, hours: String((parseInt(form.hours) || 0) + 5) })}
                  className="w-10 h-10 rounded-lg border border-slate-300 hover:bg-slate-50 font-bold text-lg text-slate-700"
                  data-testid="socwork-hours-inc"
                  title="Increase by 5"
                >+</button>
              </div>
              <div className="text-[10px] text-slate-400 mt-1">Use −/+ buttons to adjust by 5 hours, or type directly.</div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Work Type</label>
              <input
                value={form.work_type}
                onChange={(e) => setForm({ ...form, work_type: e.target.value.toUpperCase() })}
                placeholder="e.g. TEACHING, HEALTH AWARENESS"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-amber-500 outline-none uppercase"
                data-testid="socwork-work-type-input"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Issue Date</label>
              <input
                type="date"
                value={form.issue_date}
                onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-amber-500 outline-none"
                data-testid="socwork-issue-date-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Work Start Date</label>
                <input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-amber-500 outline-none"
                  data-testid="socwork-start-date-input"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Work End Date</label>
                <input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-amber-500 outline-none"
                  data-testid="socwork-end-date-input"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Performance Grade</label>
              <select
                value={form.grade}
                onChange={(e) => setForm({ ...form, grade: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-amber-500 outline-none"
                data-testid="socwork-grade-select"
              >
                <option value="A+">A+ (Outstanding)</option>
                <option value="A">A (Excellent)</option>
                <option value="B+">B+ (Very Good)</option>
                <option value="B">B (Good)</option>
                <option value="C">C (Satisfactory)</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setIssueFor(null)} className="px-4 py-2 text-sm">Cancel</button>
            <button
              onClick={submitIssue}
              disabled={issuing}
              className="bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg px-5 py-2 text-sm disabled:opacity-60"
              data-testid="socwork-issue-submit"
            >
              {issuing ? "Generating..." : "Issue Certificate"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
